import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/axios';
import { NotificationBell } from '../NotificationBell';

type NavItem = { to: string; labelKey: string; icon: string; roles?: string[] };
type NavGroup = { titleKey?: string; items: NavItem[] };

// Grouped navigation — same routes/role-gating as before; icons reference the kit sprite.
const NAV_GROUPS: NavGroup[] = [
  { items: [{ to: '/dashboard', labelKey: 'nav.dashboard', icon: 'i-dashboard' }] },
  {
    titleKey: 'nav.group.sales',
    items: [
      { to: '/pos', labelKey: 'nav.pos', icon: 'i-pos' },
      { to: '/exchange', labelKey: 'nav.exchange', icon: 'i-exchange' },
      { to: '/refund', labelKey: 'nav.refund', icon: 'i-refund' },
    ],
  },
  {
    titleKey: 'nav.group.inventory',
    items: [
      { to: '/products', labelKey: 'nav.products', icon: 'i-tag' },
      { to: '/emi-processing', labelKey: 'nav.emi_processing', icon: 'i-card' },
      { to: '/services', labelKey: 'nav.services', icon: 'i-wrench' },
      { to: '/inventory', labelKey: 'nav.inventory', icon: 'i-box' },
      { to: '/haier-orders', labelKey: 'nav.orderFromHaier', icon: 'i-receipt' },
      { to: '/vendors', labelKey: 'nav.vendors', icon: 'i-truck' },
    ],
  },
  {
    titleKey: 'nav.group.customers_finance',
    items: [
      { to: '/customers', labelKey: 'nav.customers', icon: 'i-users' },
      { to: '/hire-purchase', labelKey: 'nav.hirePurchase', icon: 'i-landmark' },
      { to: '/support', labelKey: 'nav.support', icon: 'i-ticket' },
    ],
  },
  {
    titleKey: 'nav.group.operations',
    items: [
      { to: '/reports', labelKey: 'nav.reports', icon: 'i-chart' },
      { to: '/settings', labelKey: 'nav.settings', icon: 'i-settings', roles: ['dealer_admin', 'dealer_manager'] },
    ],
  },
];

const COLLAPSE_KEY = 'pos-sidebar-collapsed';

function Icon({ id, className = 'icon' }: { id: string; className?: string }) {
  return (
    <svg className={className} aria-hidden="true">
      <use href={`#${id}`} />
    </svg>
  );
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shop, setShop] = useState<{ name: string; address: string | null } | null>(null);

  // Lightweight tenant pill — best-effort shop name; hidden if unavailable.
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ data: { settings?: { businessName?: string; address?: string | null } } }>('/dealer/settings')
      .then((res) => {
        const s = res.data?.data?.settings;
        if (!cancelled && s?.businessName) setShop({ name: s.businessName, address: s.address ?? null });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const lang = i18n.language?.startsWith('bn') ? 'bn' : 'en';
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const n = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, n ? '1' : '0');
      } catch {
        /* ignore */
      }
      return n;
    });
  };
  const onHamburger = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) toggleCollapsed();
    else setMobileOpen((o) => !o);
  };
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => !it.roles || (user && it.roles.includes(user.role))),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="app">
      {/* Sidebar */}
      <aside className={`sidebar${collapsed ? ' is-rail' : ''}${mobileOpen ? ' is-open' : ''}`}>
        <div className="brand-block">
          <div className="brand-mark">H</div>
          <div className="brand-lockup">
            <span className="brand-title">{t('nav.brand.title')}</span>
            <span className="brand-sub">{t('nav.brand.subtitle')}</span>
          </div>
        </div>

        {shop && (
          <div className="tenant-pill">
            <div className="tenant-avatar">{shop.name.charAt(0).toUpperCase()}</div>
            <div className="tenant-lines">
              <span className="tn-1">{shop.name}</span>
              {shop.address && <span className="tn-2">{shop.address}</span>}
            </div>
          </div>
        )}

        <nav className="nav">
          {visibleGroups.map((g, gi) => (
            <div key={gi}>
              {g.titleKey && <div className="nav-section">{t(g.titleKey)}</div>}
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={collapsed ? t(item.labelKey) : undefined}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}
                >
                  <Icon id={item.icon} />
                  <span className="ni-label">{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {user && (
          <div className="user-block">
            {collapsed ? (
              <>
                <button
                  onClick={() => navigate('/profile')}
                  title={user.name}
                  className="user-avatar"
                  style={{ border: 0, padding: 0, cursor: 'pointer' }}
                >
                  {user.name.charAt(0).toUpperCase()}
                </button>
                <button onClick={handleLogout} title={t('auth.logout')} className="icon-btn">
                  <Icon id="i-logout" className="icon icon-sm" />
                </button>
              </>
            ) : (
              <>
                <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                <div className="user-lines">
                  <span className="un">{user.name}</span>
                  <span className="ur">{user.role.replace(/_/g, ' ')}</span>
                </div>
                <button onClick={() => navigate('/profile')} title={t('profile.title')} className="icon-btn">
                  <Icon id="i-settings" className="icon icon-sm" />
                </button>
                <button onClick={handleLogout} title={t('auth.logout')} className="icon-btn">
                  <Icon id="i-logout" className="icon icon-sm" />
                </button>
              </>
            )}
          </div>
        )}
      </aside>

      {/* Mobile scrim */}
      {mobileOpen && <div className="scrim" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="main">
        <header className="topbar">
          <button onClick={onHamburger} title={t('nav.toggleMenu')} className="icon-btn">
            <Icon id="i-menu" />
          </button>
          <div className="spacer" />
          <div className="lang-toggle">
            <button onClick={() => i18n.changeLanguage('en')} className={`seg${lang === 'en' ? ' is-active' : ''}`}>
              {t('common.lang_en')}
            </button>
            <button onClick={() => i18n.changeLanguage('bn')} className={`seg${lang === 'bn' ? ' is-active' : ''}`}>
              {t('common.lang_bn')}
            </button>
          </div>
          <NotificationBell />
          <button onClick={() => navigate('/pos')} className="btn btn-primary btn-sm">
            <Icon id="i-plus" className="icon icon-sm" /> {t('nav.newSale')}
          </button>
        </header>

        {children}
      </div>
    </div>
  );
}
